// Network — everything and everyone, and what joins them.
//
// This replaces the Impact view, which drew one edge kind (work @mentioning work) as a
// left-to-right flow. That layout asserted a direction the data doesn't have. The graph
// now carries every connection in the log across all four kinds, laid out by force, so
// the clusters are the relationships and the bridges are the work that travels.
//
// The last lens is the exception, and deliberately so. **Ecosystem** drops back to the
// one edge kind that DOES have a direction — work whose description claims other work
// as an ancestor — and draws it in the client page's banded layout, over the whole log
// instead of one client's corner of it. Same renderer, same rules about what may be
// named; the only difference is that with no client at the centre the bands are
// generations from a root rather than above/below "their work". The two views are not
// redundant: force answers "what is near what", bands answer "what came out of what".

import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { EmptyState } from '../components/common'
import { EcosystemGraph } from '../components/EcosystemGraph'
import { NetworkCanvas } from '../components/NetworkCanvas'
import { View, type Lens } from '../components/Page'
import { usePeek, useStickyToggle } from '../components/usePeek'
import { accentVar, KINDS, type EntityKind } from '../lib/model'
import {
  buildNetwork,
  connectedOnly,
  corpusBands,
  filterKinds,
  EDGE_LABEL,
  EDGE_ORDER,
} from '../lib/network'
import { allConnections } from '../lib/reducer'
import { useStore } from '../lib/store'

/** The lenses over the force graph: the whole thing, or one side of it. */
const SCOPES: { key: string; label: string; kinds: EntityKind[]; color?: string }[] = [
  { key: 'all', label: 'Everything', kinds: [] },
  { key: 'money', label: 'Deals & orgs', kinds: ['deal', 'org', 'person'], color: 'ember' },
  { key: 'work', label: 'Work', kinds: ['project'], color: 'marine' },
  { key: 'delivery', label: 'Work & deals', kinds: ['project', 'deal'], color: 'green' },
]

/** The one lens that is a different drawing rather than a narrower one. */
const ECO = 'ecosystem'

/** Nothing is the subject of the corpus picture — there is no client at its centre. */
const NOBODY: Set<string> = new Set()

export function NetworkPage() {
  const { state, version } = useStore()
  const { peek, openPeek } = usePeek()
  const navigate = useNavigate()
  const [scope, setScope] = useState('all')
  const [loners, setLoners] = useStickyToggle('crum.network.loners', false)

  // One build, shared by the canvas and by every lens's count. It was being run
  // five times a render.
  const whole = useMemo(() => {
    void version
    return buildNetwork(state)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version])

  const net = useMemo(() => {
    const kinds = SCOPES.find((s) => s.key === scope)?.kinds ?? []
    const scoped = filterKinds(whole, kinds)
    return loners ? scoped : connectedOnly(scoped)
  }, [whole, scope, loners])

  // Ancestry only, and every one of them: this is the corpus, so there is nothing to
  // scope it to.
  const conns = useMemo(() => {
    void version
    return allConnections(state)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version])
  const bands = useMemo(() => corpusBands(conns), [conns])
  const inWeb = useMemo(() => new Set(conns.flatMap((c) => [c.from, c.to])).size, [conns])

  const lenses: Lens[] = [
    ...SCOPES.map((s) => ({
      key: s.key,
      label: s.label,
      color: s.color,
      count: (s.kinds.length ? filterKinds(whole, s.kinds) : whole).nodes.length,
    })),
    { key: ECO, label: 'Ecosystem', color: 'brass', count: inWeb },
  ]

  if (scope === ECO) {
    return (
      <View
        lenses={lenses}
        active={scope}
        onLens={setScope}
        actions={
          <span className="meta">
            {inWeb} records · {conns.length} connections
          </span>
        }
      >
        {conns.length === 0 ? (
          <EmptyState
            icon="Network"
            title="No lineage yet"
            hint={
              'Read out of descriptions: a project or artifact whose prose mentions another one is saying it came out of that work.'
            }
          />
        ) : (
          <>
            <div
              className="rounded-control border p-2"
              style={{ borderColor: 'var(--border)', background: 'var(--panel)' }}
            >
              <EcosystemGraph
                state={state}
                edges={conns}
                bands={bands}
                emphasis={NOBODY}
                onOpen={(id) => navigate(`/e/${id}`)}
              />
            </div>
            <EcosystemLegend />
          </>
        )}
      </View>
    )
  }

  if (net.edges.length === 0) {
    return (
      <View lenses={lenses} active={scope} onLens={setScope}>
        <EmptyState
          icon="Network"
          title="Nothing connected yet"
          hint="Built from deals, their clients and projects, and @mentions."
        />
      </View>
    )
  }

  return (
    <View
      lenses={lenses}
      active={scope}
      onLens={setScope}
      hint="Drag to rearrange · scroll to zoom · click to peek"
      actions={
        <>
          <button
            type="button"
            onClick={() => setLoners(!loners)}
            className="rounded-control px-2 py-1"
            style={{
              fontFamily: 'var(--mono)',
              fontSize: 'var(--text-micro)',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: loners ? 'var(--marine)' : 'var(--faint)',
            }}
            title="Records with no connections at all"
          >
            {loners ? 'showing' : 'hiding'} unconnected
          </button>
          <span className="meta">
            {net.nodes.length} records · {net.edges.length} links
          </span>
        </>
      }
      full
    >
      <NetworkCanvas net={net} onOpen={openPeek} focus={peek} />
      <Legend />
    </View>
  )
}

/**
 * What the colours and the dashes mean.
 *
 * It used to float ON the graph — `absolute bottom-3`, three wrapped rows of it —
 * so the key and the thing it was a key to were drawn on top of each other, and the
 * interaction hint ran into the edge labels. It is a DOCKED strip now: one line,
 * on the page ground, under a hairline, with the graph ending where it begins. The
 * "drag to rearrange" half moved into the view bar's hint, which is where every
 * other view in the app puts a line like that.
 */
function Legend() {
  return (
    <div
      className="pointer-events-none absolute right-0 bottom-0 left-0 flex flex-wrap items-center gap-x-4 gap-y-1 border-t px-4 py-2"
      style={{ background: 'var(--bg)', borderColor: 'var(--border)' }}
    >
      {(Object.keys(KINDS) as EntityKind[]).map((kind) => {
        const hue = `var(${accentVar(kind)})`
        return (
          <span key={kind} className="inline-flex items-center gap-1.5">
            {/* Same shape as the node it stands for: circle, diamond, square, ring. */}
            <span
              className="h-2.5 w-2.5 border"
              style={{
                borderColor: hue,
                background: `color-mix(in srgb, ${hue} 22%, transparent)`,
                borderRadius: kind === 'org' ? 3 : '50%',
                transform: kind === 'deal' ? 'rotate(45deg)' : undefined,
                boxShadow: kind === 'person' ? `inset 0 0 0 1.5px ${hue}` : undefined,
              }}
            />
            <span className="meta" style={{ color: hue }}>
              {KINDS[kind].plural}
            </span>
          </span>
        )
      })}
      <span className="meta ml-auto hidden sm:inline">
        {EDGE_ORDER.filter((k) => k !== 'mention')
          .map((k) => EDGE_LABEL[k])
          .join(' · ')}
        {' · dashed = @mention'}
      </span>
    </div>
  )
}

/**
 * What the rings and the dashes mean in the Ecosystem lens.
 *
 * This is the team's view of the whole log, so every label is real. The rings say
 * how each record would read on a client page that is not its own: solid when the
 * work is marked nameable AND its client has agreed to be named, dashed otherwise.
 * A dashed node is not hidden here — it is drawn in its right place, because the
 * shape of the web is not the private part, only the labels are.
 */
function EcosystemLegend() {
  return (
    <div className="meta mt-2 flex flex-wrap items-center gap-x-5 gap-y-1">
      <span className="flex items-center gap-1.5">
        <svg width="11" height="11">
          <circle
            cx="5.5"
            cy="5.5"
            r="4.5"
            fill="var(--panel)"
            stroke="var(--marine)"
            strokeWidth="1.5"
          />
        </svg>
        May be named elsewhere
      </span>
      <span className="flex items-center gap-1.5">
        <svg width="11" height="11">
          <circle
            cx="5.5"
            cy="5.5"
            r="4.5"
            fill="var(--panel)"
            stroke="var(--faint)"
            strokeWidth="1.5"
            strokeDasharray="3 2.5"
          />
        </svg>
        Internal, or not cleared to be named
      </span>
      <span>Dashed line · internal only</span>
      <span>Roots on top · each band built on the one above</span>
      <span className="ml-auto">hover a line for the sentence · click to open</span>
    </div>
  )
}
